import { useEffect, useRef, useState } from "react";
import {
  Room,
  RoomEvent,
  Track,
  type RemoteAudioTrack,
  type RemoteParticipant,
  type RemoteVideoTrack,
} from "livekit-client";

export interface ParticipantMedia {
  connected: boolean;
  video?: RemoteVideoTrack;
  audio?: RemoteAudioTrack;
}

type Status = "connecting" | "connected" | "error";

/// Subscribe-only connection to the term's proctoring room. Candidates' own track permissions
/// decide (at the SFU) which streams this staff member actually receives.
export const useProctoringRoom = (serverUrl?: string, token?: string) => {
  const [media, setMedia] = useState<Record<string, ParticipantMedia>>({});
  const [status, setStatus] = useState<Status>("connecting");
  const [error, setError] = useState<string | null>(null);
  // Session refetches return a fresh token; only the first one is used to connect.
  const tokenRef = useRef(token);
  if (!tokenRef.current && token) tokenRef.current = token;
  const firstToken = tokenRef.current;

  useEffect(() => {
    if (!serverUrl || !firstToken) return;
    let disposed = false;
    const room = new Room({ adaptiveStream: true, dynacast: true });

    // Always re-read the participant currently in the room: a reconnect with the same identity can
    // deliver the old session's disconnect/unsubscribe events after the new session is already live.
    const refresh = (identity: string) => {
      if (disposed) return;
      const participant = room.remoteParticipants.get(identity);
      if (!participant) {
        setMedia((prev) => ({ ...prev, [identity]: { connected: false } }));
        return;
      }
      let video: RemoteVideoTrack | undefined;
      let audio: RemoteAudioTrack | undefined;
      participant.trackPublications.forEach((pub) => {
        if (!pub.track) return;
        if (pub.kind === Track.Kind.Video) video = pub.track as RemoteVideoTrack;
        if (pub.kind === Track.Kind.Audio) audio = pub.track as RemoteAudioTrack;
      });
      setMedia((prev) => ({ ...prev, [identity]: { connected: true, video, audio } }));
    };
    const onParticipant = (participant: RemoteParticipant) => refresh(participant.identity);

    room
      .on(RoomEvent.ParticipantConnected, onParticipant)
      .on(RoomEvent.ParticipantDisconnected, onParticipant)
      .on(RoomEvent.TrackSubscribed, (_t, _p, participant) => onParticipant(participant))
      .on(RoomEvent.TrackUnsubscribed, (_t, _p, participant) => onParticipant(participant))
      .on(RoomEvent.TrackMuted, (_p, participant) => refresh(participant.identity))
      .on(RoomEvent.TrackUnmuted, (_p, participant) => refresh(participant.identity))
      .on(RoomEvent.Disconnected, () => !disposed && setStatus("error"));

    room
      .connect(serverUrl, firstToken)
      .then(() => {
        if (disposed) return;
        setStatus("connected");
        room.remoteParticipants.forEach(onParticipant);
      })
      .catch((e) => {
        console.error("LiveKit proctoring connect error:", e);
        if (!disposed) {
          setStatus("error");
          setError(e?.message ?? null);
        }
      });

    return () => {
      disposed = true;
      room.disconnect();
    };
  }, [serverUrl, firstToken]);

  return { media, status, error };
};
